import React, { useMemo, useRef } from "react";
import { PortfolioItem } from "@/hooks/useProfile";
import ImagesHeader from "./images/ImagesHeader";
import ImageGallery from "./images/ImageGallery";
import { usePortfolioImageOperations } from "../hooks/usePortfolioImageOperations";
import { normalizePortfolioItem } from "@/hooks/portfolio/usePortfolioNormalizer";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AddImageCardHandle } from "./images/AddImageCard";

interface ProjectImagesManagerProps {
  item: PortfolioItem;
  onChange: (updatedItem: PortfolioItem) => void;
  onUploadStatusChange?: (isUploading: boolean) => void;
}

const ProjectImagesManager = ({
  item,
  onChange,
  onUploadStatusChange,
}: ProjectImagesManagerProps) => {
  // Normalize item once and memoize it
  const normalizedItem = React.useMemo(
    () => normalizePortfolioItem(item),
    [item]
  );
  const [errorKey, setErrorKey] = React.useState(0);

  // Use dedicated hooks for image operations and validation
  const {
    editingImageId,
    setEditingImageId,
    isImageUploading,
    handleAddImages,
    handleRemoveImage,
    handleMoveImage,
    handleUpdateImageCaption,
    handleImageUploadingChange,
  } = usePortfolioImageOperations({
    item: normalizedItem,
    onChange,
  });

  const images = useMemo(
    () => normalizedItem.images || [],
    [normalizedItem.images]
  );

  // Update parent component about upload status
  React.useEffect(() => {
    onUploadStatusChange?.(isImageUploading);
  }, [isImageUploading, onUploadStatusChange]);

  // Handle error boundary resets
  const handleReset = React.useCallback(() => {
    setErrorKey((prev) => prev + 1);
  }, []);

  // Map direction for image movement
  const handleImageMove = React.useCallback(
    (imageId: string, direction: "left" | "right") => {
      handleMoveImage(imageId, direction === "left" ? "up" : "down");
    },
    [handleMoveImage]
  );

  const addImageCardRef = useRef<AddImageCardHandle>(null);

  return (
    <ErrorBoundary key={errorKey} onReset={handleReset}>
      <div className="space-y-4">
        <ImagesHeader
          imagesCount={images.length}
          onAddClick={() => addImageCardRef.current?.openFilePicker()}
        />
        <ImageGallery
          addImageCardRef={addImageCardRef}
          images={images}
          editingImageId={editingImageId}
          onEditImage={setEditingImageId}
          onRemoveImage={handleRemoveImage}
          onMoveImage={handleImageMove}
          onUpdateImageCaption={handleUpdateImageCaption}
          onAddImages={handleAddImages}
          onUploadStatusChange={handleImageUploadingChange}
        />
      </div>
    </ErrorBoundary>
  );
};

export default ProjectImagesManager;
